import { PublicClientApplication, LogLevel } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';

const scopes = ['openid', 'profile', 'User.Read', 'Group.Read.All', 'Directory.Read.All'];

export default class AzureAD {
    constructor(config, Vue) {
        this.Vue = Vue;
        this.config = config;

        // MSAL Browser configuration
        const msalConfig = {
            auth: {
                clientId: this.config.clientId,
                authority: 'https://login.microsoftonline.com/common',
                redirectUri: this.config.redirectUri,
                postLogoutRedirectUri: window.location.origin,
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: this.config.cacheLocation || 'localStorage',
                storeAuthStateInCookie: false
            },
            system: {
                loggerOptions: {
                    loggerCallback: this.loggerCallback.bind(this),
                    logLevel: LogLevel.Verbose,
                    piiLoggingEnabled: false
                }
            }
        };

        this.msal = new PublicClientApplication(msalConfig);
        this.isInitialized = false;
        this.initPromise = null;

        // Initialize MSAL and handle any pending redirects
        this.initPromise = this.msal.initialize().then(() => {
            return this.msal.handleRedirectPromise();
        }).then(response => {
            this.isInitialized = true;
            if (response) {
                console.log('Initial redirect response:', response);
            }
            return response;
        }).catch(err => {
            this.isInitialized = true;
            console.error('MSAL initialization error:', err);
        });

        // Setup client for Microsoft Graph API
        this.client = Client.init({
            authProvider: async (done) => {
                try {
                    const account = this.getAccount();
                    if (!account) {
                        done(new Error('No account found'), null);
                        return;
                    }

                    const response = await this.msal.acquireTokenSilent({
                        scopes: scopes,
                        account: account
                    });

                    done(null, response.accessToken);
                } catch (error) {
                    if (error.name === 'InteractionRequiredAuthError') {
                        try {
                            const response = await this.msal.acquireTokenPopup({
                                scopes: scopes
                            });
                            done(null, response.accessToken);
                        } catch (popupError) {
                            done(popupError, null);
                        }
                    } else {
                        done(error, null);
                    }
                }
            }
        });
    }

    loggerCallback(level, message, containsPii) {
        if (containsPii) {
            return;
        }
        console.log(message);
    }

    getAccount() {
        const accounts = this.msal.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    }

    async refreshToken(callback, errorCallback) {
        console.log('refreshing token');

        try {
            const account = this.getAccount();
            if (!account) {
                throw new Error('No account found');
            }

            const response = await this.msal.acquireTokenSilent({
                scopes: [this.config.clientId],
                account: account
            });

            console.log('token refreshed at ' + new Date());
            console.log(response);

            if (callback) {
                callback(response.accessToken);
            }
        } catch (err) {
            console.error('error refreshing token');
            console.error(err);
            if (errorCallback) {
                errorCallback(err);
            }
        }
    }

    async login() {
        try {
            // Wait for MSAL initialization to complete
            if (!this.isInitialized) {
                console.log('Waiting for MSAL initialization...');
                await this.initPromise;
            }

            console.log('Starting login redirect...');
            await this.msal.loginRedirect({
                scopes: scopes
            });
        } catch (error) {
            console.error('Login error:', error);
        }
    }

    async logout(cb) {
        try {
            const account = this.getAccount();
            await this.msal.logoutRedirect({
                account: account,
                postLogoutRedirectUri: window.location.origin
            });
        } catch (error) {
            console.error('Logout error:', error);
            // Fallback: clear localStorage
            localStorage.clear();
            if (cb) {
                cb();
            }
        }
    }

    isLoggedIn() {
        const account = this.getAccount();
        return account !== null;
    }

    isLoginExpired() {
        const account = this.getAccount();
        if (!account) {
            return true;
        }

        // Check if account has idTokenClaims with expiration
        if (account.idTokenClaims && account.idTokenClaims.exp) {
            const compDate = new Date().getTime() / 1000;
            return compDate > account.idTokenClaims.exp;
        }

        return false;
    }

    hasError() {
        // MSAL Browser handles errors differently
        // You can check if there's an error in localStorage if needed
        return false;
    }

    getError() {
        // Return null as MSAL Browser manages errors internally
        return null;
    }

    async getUserGroups() {
        if (!this.isLoggedIn()) {
            console.log('getUserGroups: not logged in');
            return Promise.resolve([]);
        }

        // first check if groups already have been loaded
        var jsonGroups = JSON.parse(localStorage.getItem('taz.groups'));

        if (jsonGroups) {
            console.log('getUserGroups: already loaded. checking expiration');

            // then check if they are expired
            var groupExpired = localStorage.getItem('taz.groups.exp');
            var compDate = new Date().getTime() / 1000;

            if (compDate < groupExpired) {
                console.log('getUserGroups: still valid...');
                return Promise.resolve(jsonGroups);
            } else {
                console.log('getUserGroups: groups expired');
            }
        }

        // otherwise load them from azure
        var groupFilter = 'taz'; // filter || this.config.groupFilterPrefix

        try {
            const groupsResp = await this.client
                .api('/groups')
                .filter(`startswith(displayName, '${groupFilter}')`)
                .select('id', 'displayName')
                .get();

            const userGroupsResp = await this.client
                .api('/me/getMemberObjects')
                .post({ securityEnabledOnly: true });

            console.log('getUserGroups: received graph response');

            const groups = groupsResp.value;
            const userGroups = userGroupsResp.value;
            const result = groups.filter(g => userGroups.indexOf(g.id) > -1);

            // save loaded groups to local storage
            localStorage.setItem('taz.groups', JSON.stringify(result));
            // add expiration
            var ts = new Date().getTime() + 60 * 60 * 1000; // 1 h
            var newExpiration = parseInt(ts / 1000);
            localStorage.setItem('taz.groups.exp', newExpiration);

            return Promise.resolve(result);
        } catch (err) {
            console.error('getUserGroups error:', err);
            return Promise.resolve([]);
        }
    }

    isInGroup(groups, requiredGroup) {
        return groups.filter(f => f.displayName === requiredGroup).length > 0;
    }
}
